// src/components/DashboardCustomizer.jsx
//
// Drawer / dialog that lets the user toggle dashboard widgets on/off and
// reorder them. Pairs with `useDashboardLayout`.
//
// Reordering supports both:
//   • Native HTML5 drag-and-drop — grab the grip handle, drop on another
//     item to place before/after it (based on pointer-over-half).
//   • Up/down arrow buttons — keyboard-accessible fallback; also kept
//     for users on coarse-pointer devices where HTML5 DnD is fiddly.

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  RestartAlt as ResetIcon,
  DragIndicator as GripIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const DashboardCustomizer = ({ open, onClose, layout }) => {
  const { widgets, toggle, moveUp, moveDown, moveTo, reset } = layout;

  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { id, position: 'before'|'after' }

  const onDragStart = (e, id) => {
    setDragId(id);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
    } catch { /* Safari sometimes throws here */ }
  };

  const onDragOver = (e, id) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const half = rect.top + rect.height / 2;
    const position = e.clientY < half ? 'before' : 'after';
    setDropTarget((prev) =>
      prev?.id === id && prev.position === position ? prev : { id, position },
    );
  };

  const onDragLeaveList = () => {
    setDropTarget(null);
  };

  const onDrop = (e, id) => {
    e.preventDefault();
    if (!dragId || dragId === id) {
      setDragId(null);
      setDropTarget(null);
      return;
    }
    const fromIdx = widgets.findIndex((w) => w.id === dragId);
    let toIdx = widgets.findIndex((w) => w.id === id);
    if (fromIdx < 0 || toIdx < 0) {
      setDragId(null);
      setDropTarget(null);
      return;
    }
    if (dropTarget?.position === 'after') toIdx += 1;
    // Splice math: removing earlier source shifts the target left by one.
    if (fromIdx < toIdx) toIdx -= 1;
    moveTo(dragId, toIdx);
    setDragId(null);
    setDropTarget(null);
  };

  const onDragEnd = () => {
    setDragId(null);
    setDropTarget(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Customize dashboard
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag widgets by the grip handle to reorder, or use the arrow
          buttons. Toggle the switch to show or hide each one.
        </Typography>
        <List dense sx={{ py: 0 }} onDragLeave={onDragLeaveList}>
          {widgets.map((w, idx) => {
            const isDragging = dragId === w.id;
            const dropHere = dropTarget?.id === w.id;
            const borderTop = dropHere && dropTarget.position === 'before'
              ? '2px solid' : '2px solid transparent';
            const borderBottom = dropHere && dropTarget.position === 'after'
              ? '2px solid' : '2px solid transparent';
            return (
              <ListItem
                key={w.id}
                draggable
                onDragStart={(e) => onDragStart(e, w.id)}
                onDragOver={(e) => onDragOver(e, w.id)}
                onDrop={(e) => onDrop(e, w.id)}
                onDragEnd={onDragEnd}
                sx={{
                  borderRadius: 1,
                  borderTop,
                  borderBottom,
                  borderTopColor: 'primary.main',
                  borderBottomColor: 'primary.main',
                  '&:hover': { bgcolor: 'action.hover' },
                  gap: 0.5,
                  cursor: isDragging ? 'grabbing' : 'default',
                  opacity: isDragging ? 0.5 : 1,
                  transition: 'opacity 120ms ease',
                }}
                secondaryAction={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          size="small"
                          disabled={idx === 0}
                          onClick={() => moveUp(w.id)}
                          aria-label={`Move ${w.label} up`}
                        >
                          <UpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={idx === widgets.length - 1}
                          onClick={() => moveDown(w.id)}
                          aria-label={`Move ${w.label} down`}
                        >
                          <DownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Switch
                      size="small"
                      checked={w.enabled}
                      onChange={() => toggle(w.id)}
                      inputProps={{ 'aria-label': `Show ${w.label}` }}
                    />
                  </Box>
                )}
              >
                <Tooltip title="Drag to reorder">
                  <Box
                    component="span"
                    sx={{
                      color: 'text.disabled',
                      mr: 0.5,
                      display: 'inline-flex',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <GripIcon fontSize="small" />
                  </Box>
                </Tooltip>
                <ListItemText
                  primary={w.label}
                  secondary={w.description}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: w.enabled ? 600 : 400 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                  sx={{ opacity: w.enabled ? 1 : 0.55 }}
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button startIcon={<ResetIcon />} onClick={reset} color="inherit">
          Reset to defaults
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardCustomizer;
